import '@mdxeditor/editor/style.css'

import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CodeToggle,
  CreateLink,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  headingsPlugin,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  type MDXEditorMethods,
  quotePlugin,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from '@mdxeditor/editor'
import * as React from 'react'

import { cn } from '../../utils/cn'

type MarkdownEditorProps = {
  value: string
  onChange: (value: string) => void
  className?: string
  contentEditableClassName?: string
  placeholder?: string
  readOnly?: boolean
  compact?: boolean
}

export function MarkdownEditor({
  value,
  onChange,
  className,
  contentEditableClassName,
  placeholder,
  readOnly,
  compact,
}: MarkdownEditorProps) {
  const ref = React.useRef<MDXEditorMethods>(null)

  return (
    <div className={cn('border-input bg-surface overflow-hidden rounded-md border', className)}>
      <MDXEditor
        ref={ref}
        markdown={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        contentEditableClassName={cn(
          'prose prose-sm max-w-none px-4 py-3 focus:outline-none',
          '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&>*:last-child]:pb-0',
          compact ? 'min-h-[100px]' : 'min-h-[200px]',
          contentEditableClassName
        )}
        plugins={[
          headingsPlugin(),
          listsPlugin(),
          quotePlugin(),
          thematicBreakPlugin(),
          linkPlugin(),
          linkDialogPlugin(),
          tablePlugin(),
          markdownShortcutPlugin(),
          diffSourcePlugin({ viewMode: 'rich-text' }),
          ...(readOnly
            ? []
            : [
                toolbarPlugin({
                  toolbarClassName: 'rounded-none! border-b! border-input! bg-neutral-50!',
                  toolbarContents: () => (
                    <DiffSourceToggleWrapper options={['rich-text', 'source']}>
                      <UndoRedo />
                      <Separator />
                      <BoldItalicUnderlineToggles />
                      {!compact && <CodeToggle />}
                      {!compact && <Separator />}
                      {!compact && <BlockTypeSelect />}
                      <Separator />
                      <ListsToggle options={compact ? ['bullet'] : ['bullet', 'number', 'check']} />
                      <Separator />
                      <CreateLink />
                      <Separator />
                      <InsertTable />
                    </DiffSourceToggleWrapper>
                  ),
                }),
              ]),
        ]}
      />
    </div>
  )
}
